/**
 * RSSContentModal Component
 *
 * Modal for viewing RSS feed items with source article preview.
 * Includes action button to select for draft generation.
 * Redesigned for better mobile usability and visual appeal.
 */

import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

function RSSContentModal({ open, item, onClose, onSelect, isProcessing = false }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (!item) return null;

  // Check if item has Processing status from Supabase
  const isStatusProcessing = item.status === "Processing";
  const showProcessing = isProcessing || isStatusProcessing;
  const isSelectable = item.status === "New" && !isProcessing;

  // Format date nicely
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get domain from URL
  const getDomain = (url) => {
    try {
      return new URL(url).hostname.replace("www.", "");
    } catch {
      return url;
    }
  };

  // Get source color based on channel
  const getSourceColor = (channel) => {
    const lowerChannel = (channel || "").toLowerCase();
    if (lowerChannel.includes("reddit")) return { bg: "#FF4500", text: "white" };
    if (lowerChannel.includes("rss")) return { bg: "#FFA500", text: "white" };
    if (lowerChannel.includes("news")) return { bg: "#1A73E8", text: "white" };
    return { bg: "#6B7280", text: "white" };
  };

  const sourceColors = getSourceColor(item.sourceChannel);

  return (
    <Dialog
      open={open}
      onClose={showProcessing ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "20px",
          overflow: "hidden",
          maxHeight: isMobile ? "100%" : "90vh",
        },
      }}
    >
      {/* Gradient Header */}
      <MDBox
        sx={{
          background: "linear-gradient(135deg, #1A73E8 0%, #0D47A1 100%)",
          pt: isMobile ? "calc(env(safe-area-inset-top) + 16px)" : 3,
          pb: 3,
          px: { xs: 2, sm: 3 },
          position: "relative",
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          disabled={showProcessing}
          sx={{
            position: "absolute",
            top: isMobile ? "calc(env(safe-area-inset-top) + 8px)" : 8,
            right: 8,
            color: "white",
            backgroundColor: "rgba(255,255,255,0.15)",
            backdropFilter: "blur(10px)",
            minWidth: "44px",
            minHeight: "44px",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.25)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Source Badge */}
        {item.sourceChannel && (
          <Chip
            label={item.sourceChannel.toUpperCase()}
            size="small"
            sx={{
              backgroundColor: sourceColors.bg,
              color: sourceColors.text,
              fontWeight: 700,
              fontSize: "0.65rem",
              letterSpacing: "0.5px",
              mb: 2,
              height: "24px",
            }}
          />
        )}

        {/* Title */}
        <MDTypography
          variant="h4"
          color="white"
          fontWeight="bold"
          sx={{
            fontSize: { xs: "1.35rem", sm: "1.5rem" },
            lineHeight: 1.3,
            pr: 4,
          }}
        >
          {item.sourceHeadline}
        </MDTypography>

        {/* Date */}
        {item.date && (
          <MDBox display="flex" alignItems="center" mt={2} gap={0.5}>
            <Icon sx={{ color: "rgba(255,255,255,0.7)", fontSize: "1rem" }}>schedule</Icon>
            <MDTypography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 500 }}
            >
              {formatDate(item.date)}
            </MDTypography>
          </MDBox>
        )}
      </MDBox>

      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {/* Summary Section */}
        <MDBox sx={{ p: { xs: 2.5, sm: 3 } }}>
          <MDTypography
            variant="body1"
            color="text"
            sx={{
              lineHeight: 1.8,
              fontSize: { xs: "0.95rem", sm: "1rem" },
              color: "#374151",
            }}
          >
            {item.sourceSummary}
          </MDTypography>
        </MDBox>

        <Divider sx={{ mx: 3 }} />

        {/* Source Link Card */}
        {item.sourceURL && (
          <MDBox sx={{ p: { xs: 2.5, sm: 3 } }}>
            <MDBox
              onClick={() => window.open(item.sourceURL, "_blank")}
              sx={{
                display: "flex",
                alignItems: "center",
                p: 2,
                borderRadius: "12px",
                backgroundColor: "#F8FAFC",
                border: "1px solid #E2E8F0",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#F1F5F9",
                  borderColor: "#CBD5E1",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "scale(0.99)",
                },
              }}
            >
              <MDBox
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "10px",
                  backgroundColor: "#E0E7FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ color: "#4F46E5" }}>article</Icon>
              </MDBox>
              <MDBox sx={{ flex: 1, minWidth: 0 }}>
                <MDTypography
                  variant="caption"
                  fontWeight="bold"
                  sx={{ color: "#64748B", textTransform: "uppercase", letterSpacing: "0.5px" }}
                >
                  Source Article
                </MDTypography>
                <MDTypography
                  variant="body2"
                  sx={{
                    color: "#1E293B",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {getDomain(item.sourceURL)}
                </MDTypography>
              </MDBox>
              <Icon sx={{ color: "#94A3B8", ml: 1 }}>open_in_new</Icon>
            </MDBox>
          </MDBox>
        )}

        {/* Info Card */}
        <MDBox sx={{ px: { xs: 2.5, sm: 3 }, pb: { xs: 2.5, sm: 3 } }}>
          <MDBox
            sx={{
              display: "flex",
              alignItems: "flex-start",
              p: 2,
              borderRadius: "12px",
              backgroundColor: "#EFF6FF",
              border: "1px solid #BFDBFE",
            }}
          >
            <MDBox
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                backgroundColor: "#DBEAFE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 1.5,
                flexShrink: 0,
              }}
            >
              <Icon sx={{ color: "#3B82F6", fontSize: "1.1rem" }}>auto_awesome</Icon>
            </MDBox>
            <MDTypography
              variant="body2"
              sx={{ color: "#1E40AF", lineHeight: 1.5, fontSize: "0.85rem" }}
            >
              Selecting this will use AI to generate a full blog post with title, content, and
              social media copies.
            </MDTypography>
          </MDBox>
        </MDBox>
      </DialogContent>

      {/* Sticky Action Bar */}
      <MDBox
        sx={{
          p: { xs: 2, sm: 2.5 },
          pb: isMobile ? "calc(env(safe-area-inset-bottom) + 16px)" : 2.5,
          borderTop: "1px solid #E5E7EB",
          backgroundColor: "white",
          display: "flex",
          gap: 1.5,
          flexDirection: { xs: "row", sm: "row" },
        }}
      >
        {showProcessing ? (
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={2}
            width="100%"
            py={1}
          >
            <CircularProgress size={24} sx={{ color: "#1A73E8" }} />
            <MDTypography variant="body2" sx={{ color: "#6B7280" }}>
              Generating draft... Check the Drafts tab soon.
            </MDTypography>
          </MDBox>
        ) : (
          <>
            <MDButton
              onClick={onClose}
              color="light"
              variant="contained"
              sx={{
                flex: { xs: "0 0 auto", sm: "0 0 auto" },
                minHeight: "48px",
                px: 3,
                borderRadius: "12px",
                backgroundColor: "#F3F4F6",
                color: "#374151",
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#E5E7EB",
                  boxShadow: "none",
                },
              }}
            >
              Cancel
            </MDButton>
            <MDButton
              onClick={() => onSelect(item)}
              color="info"
              variant="gradient"
              disabled={!isSelectable}
              startIcon={<Icon>add_circle</Icon>}
              sx={{
                flex: 1,
                minHeight: "48px",
                borderRadius: "12px",
                fontWeight: 600,
                textTransform: "none",
                fontSize: "0.95rem",
                background: isSelectable
                  ? "linear-gradient(135deg, #1A73E8 0%, #0D47A1 100%)"
                  : undefined,
                boxShadow: isSelectable ? "0 4px 14px rgba(26, 115, 232, 0.4)" : "none",
                "&:hover": {
                  boxShadow: isSelectable ? "0 6px 20px rgba(26, 115, 232, 0.5)" : "none",
                },
              }}
            >
              Select for Draft
            </MDButton>
          </>
        )}
      </MDBox>
    </Dialog>
  );
}

RSSContentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  item: PropTypes.shape({
    id: PropTypes.string,
    sourceHeadline: PropTypes.string,
    sourceSummary: PropTypes.string,
    sourceURL: PropTypes.string,
    sourceChannel: PropTypes.string,
    date: PropTypes.string,
    status: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  isProcessing: PropTypes.bool,
};

export default RSSContentModal;
